import { Controller } from "stimulus"

// Shows "<length>/<max>" beside a text field once it's within `threshold` characters of its
// maxlength, and hides it again below that. The limit is read from the input's own maxlength, so
// it's only ever declared once, on the field. The counter turns yellow within `warn` characters of
// the limit and red once it's reached. Pass a threshold >= the maxlength to always show it.
// Extracted from chat_panel_controller#updateCounter (the live chat composer), which keeps its own
// copy for now.
//
// Render the counter with the shared/character_count partial, which draws it inside the field's box:
//
//   <div class="relative" data-controller="character-count">
//     <textarea class="pr-16" maxlength="100" data-character-count-target="input"
//               data-action="input->character-count#update"></textarea>
//     <%= render "shared/character_count" %>
//   </div>
const NORMAL_CLASS = "text-gray-400"
const WARN_CLASS = "text-yellow-600"
const LIMIT_CLASS = "text-red-600"

export default class extends Controller {
  static targets = ["input", "counter"]
  static values = {
    threshold: { type: Number, default: 20 },
    warn: { type: Number, default: 10 },
  }

  connect() {
    // A Turbo-restored or saved field can be pre-filled, so render a correct counter immediately.
    this.update()
  }

  // max <= 0 means no maxlength attribute is set (browsers report -1), so there's nothing to count.
  update() {
    if (!this.hasInputTarget || !this.hasCounterTarget) return

    const max = this.inputTarget.maxLength
    if (max <= 0) return

    const length = this.inputTarget.value.length
    const remaining = max - length
    this.counterTarget.classList.toggle("hidden", remaining > this.thresholdValue)
    this.counterTarget.textContent = `${length}/${max}`

    // Swap rather than stack the colour utilities: with two on one element, CSS order would win.
    let colour = NORMAL_CLASS
    if (remaining <= 0) colour = LIMIT_CLASS
    else if (remaining <= this.warnValue) colour = WARN_CLASS
    this.counterTarget.classList.remove(NORMAL_CLASS, WARN_CLASS, LIMIT_CLASS)
    this.counterTarget.classList.add(colour)
  }
}
